import { DT } from "../../constants.ts";
import { add, scale } from "../../math.ts";
import { Btn } from "../../net/input.ts";
import { ActionKind, hv, setHv } from "../../sim/components.ts";
import { addOverhealth, aim, applyDamage, applyHeal, applyStatus, damageBarrier, isEnemy, lockOn, moveProjectile, playersInRadius, spawnProjectile } from "../../sim/combat.ts";
import { FxKind } from "../../sim/events.ts";
import { HeroId, Role, Slot, type HeroDef } from "../types.ts";
import { pk, type HeroModule } from "../module.ts";
import { action, commonInput, consume, ownerAlive, ready, secs, trigger, useUlt } from "../kitlib.ts";

export const V = { orbs: 0, orbTimer: 1, passive: 2, ultPulse: 3 } as const;
export const PK = { Thorn: pk(HeroId.Mantissa, 0), Spore: pk(HeroId.Mantissa, 1) } as const;

const def: HeroDef = {
  id: HeroId.Mantissa,
  slug: "mantissa",
  name: "Mantissa",
  role: Role.Support,
  color: "#7ed957",
  blurb: "Empathic tactician who spends life orbs on heals and buffs, sleeps divers, and blooms a roaming ultimate.",
  health: 275,
  armor: 0,
  shields: 0,
  radius: 0.44,
  height: 1.78,
  headRadius: 0.22,
  speed: 6,
  jumpSpeed: 7.45,
  ammo: 15,
  reloadTime: 1.5,
  ultCost: 4300,
  abilities: {
    [Slot.Primary]: { name: "Thorn Math", key: "LMB", description: "Fire a 55-damage fast thorn. Critical hits restore one Life Orb.", cooldown: 0 },
    [Slot.Secondary]: { name: "Kind Bloom", key: "RMB", description: "Spend a Life Orb to burst-heal an ally and make follow-up healing stronger.", cooldown: 0 },
    [Slot.Ability1]: { name: "Nap Spore", key: "Shift", description: "Throw a spore that sleeps one enemy.", cooldown: 15 },
    [Slot.Ability2]: { name: "Pep Pollen", key: "E", description: "Spend a Life Orb to grant an ally 8% damage boost and +1 m/s speed.", cooldown: 0 },
    [Slot.Ability3]: { name: "Prickly Mood", key: "F", description: "Spend a Life Orb to boost your own damage by 12%.", cooldown: 0 },
    [Slot.Ultimate]: { name: "Garden Overdrive", key: "Q", description: "Radiate 150 healing per second and +3 m/s speed in a 15m aura; overhealing becomes bonus health.", cooldown: 0 },
  },
  passive: { name: "Favorable Photosynthesis", description: "Life Orbs recharge over time and on critical hits. Spending one heals you over time." },
};

function spendOrb(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  const n = hv(sim.c, eid, V.orbs);
  if (n < 1) return false;
  setHv(sim.c, eid, V.orbs, n - 1);
  setHv(sim.c, eid, V.passive, 8);
  return true;
}

function restoreOrb(sim: Parameters<HeroModule["kit"]["tick"]>[0], eid: number) {
  setHv(sim.c, eid, V.orbs, Math.min(4, hv(sim.c, eid, V.orbs) + 1));
  setHv(sim.c, eid, V.orbTimer, 4);
}

const module: HeroModule = {
  def,
  kit: {
    init(sim, eid) {
      setHv(sim.c, eid, V.orbs, 4);
      setHv(sim.c, eid, V.orbTimer, 4);
    },
    tick(sim, eid, input, pressed, canAct) {
      const c = sim.c;
      if (hv(c, eid, V.orbs) < 4) {
        const t = hv(c, eid, V.orbTimer) - DT;
        if (t <= 0) restoreOrb(sim, eid);
        else setHv(c, eid, V.orbTimer, t);
      }
      if (hv(c, eid, V.passive) > 0) {
        setHv(c, eid, V.passive, hv(c, eid, V.passive) - DT);
        applyHeal(sim, eid, eid, 2.5 * 5 * DT, { silent: true });
        if (c.Health.sinceDamage[eid]! > secs(3)) applyStatus(sim, eid, "haste", 0.2, { source: eid, hastePct: 0.25 });
      }
      if (c.Ult.active[eid]! > 0) {
        setHv(c, eid, V.ultPulse, (Math.sin(sim.tick * 0.2) + 1) * 0.5);
        for (const { eid: t } of playersInRadius(sim, sim.posOf(eid), 15, (e) => !isEnemy(sim, eid, e), false)) {
          const healed = applyHeal(sim, eid, t, 15 * 10 * DT, { silent: true });
          if (healed < 15 * 10 * DT) addOverhealth(sim, t, 150, secs(2), eid);
          applyStatus(sim, t, "haste", 0.2, { source: eid, hastePct: 0.5 });
        }
      } else setHv(c, eid, V.ultPulse, 0);
      commonInput(sim, eid, input, pressed);
      if (!canAct) return;
      if (useUlt(sim, eid, input, pressed)) c.Ult.active[eid] = secs(8);
      if (pressed & Btn.Ability1 && ready(sim, eid, Slot.Ability1)) {
        consume(sim, eid, Slot.Ability1);
        const a = aim(sim, eid);
        spawnProjectile(sim, eid, { kind: PK.Spore, origin: add(a.origin, scale(a.dir, 0.5)), vel: scale(a.dir, 65), radius: 0.2, seconds: 100 / 65, seq: input.seq });
        action(sim, eid, ActionKind.Cast, 14);
      }
      if (pressed & Btn.Secondary && spendOrb(sim, eid)) {
        const t = lockOn(sim, eid, 30, 0.26, true) ?? eid;
        applyHeal(sim, eid, t, 60);
        applyStatus(sim, t, "healBoost", 8, { source: eid });
        action(sim, eid, ActionKind.Cast, 10);
      }
      if (pressed & Btn.Ability2 && spendOrb(sim, eid)) {
        const t = lockOn(sim, eid, 30, 0.26, true) ?? eid;
        applyStatus(sim, t, "boost", 8, { source: eid, boostPct: 0.08 });
        applyStatus(sim, t, "haste", 8, { source: eid, hastePct: 1 / 6 });
        action(sim, eid, ActionKind.Cast, 10);
      }
      if (pressed & Btn.Ability3 && spendOrb(sim, eid)) {
        applyStatus(sim, eid, "boost", 8, { source: eid, boostPct: 0.12 });
        action(sim, eid, ActionKind.Cast, 10);
      }
      if (trigger(sim, eid, !!(input.buttons & Btn.Primary))) {
        c.Weapon.fireCd[eid] = secs(0.4);
        const a = aim(sim, eid);
        spawnProjectile(sim, eid, { kind: PK.Thorn, origin: add(a.origin, scale(a.dir, 0.45)), vel: scale(a.dir, 150), radius: 0.1, seconds: 1.1, seq: input.seq });
        action(sim, eid, ActionKind.Fire, 7);
      }
    },
  },
  projectiles: {
    [PK.Thorn]: {
      visual: { color: "#d7ff7a", size: 0.11, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p, "enemies");
        if (hit) {
          if (hit.target !== undefined && ownerAlive(sim, owner)) {
            applyDamage(sim, hit.target, 55, owner, { crit: hit.head, pos: hit.pos });
            if (hit.head) restoreOrb(sim, owner);
          } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 55, owner);
          else sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Spore });
          sim.destroyEntity(p);
          return;
        }
        if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
    [PK.Spore]: {
      visual: { color: "#baff6a", size: 0.18, trail: true },
      tick(sim, p) {
        const owner = sim.c.Projectile.owner[p]!;
        const hit = moveProjectile(sim, p, "enemies");
        if (hit) {
          if (hit.target !== undefined && ownerAlive(sim, owner)) {
            applyDamage(sim, hit.target, 10, owner, { pos: hit.pos });
            applyStatus(sim, hit.target, "sleep", 3.5, { source: owner });
          } else if (hit.barrier !== undefined) damageBarrier(sim, hit.barrier, 10, owner);
          sim.emit({ type: "impact", pos: hit.pos, kind: FxKind.Spore });
          sim.destroyEntity(p);
          return;
        }
        if (sim.c.Projectile.ticks[p]! <= 0) sim.destroyEntity(p);
      },
    },
  },
};

export default module;
